import { ServiceType } from "~/utils/constants";
import { TClient } from "./client";
import { TUser } from "./user";
import { InventoryTemplate } from "~/services/inventory-templates/inventory-templates.types";
import { TVesselType } from "~/services/vessel/vessel.types";
import { TProject } from "./project";

// Shipping Address type (from client.shippingAddresses)
export interface ShippingAddress {
  id: string;
  addressName?: string;
  address: string;
  anotherAddress?: string;
  apt?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  type?: string;
  travelTime?: number;
  specialInstructions?: string;
  clientId?: string;
}

export enum EstimateStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  CONVERTED = 'CONVERTED',
}

export interface TempClientData {
  name: string;
  email?: string;
  phone?: string;
}

export interface TempLocationData {
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

// Expo Mode Types
export interface ExpoDayPosition {
  id: string;
  estimateServiceDayId: string;
  positionId: string;
  position?: {
    id: string;
    name: string;
    hourlyRate: number;
    positionRef?: string;
  };
  quantity: number;
  startTime: string;
  duration: number;
  hourlyRate: number;
  subtotal?: number;
}

export interface ExpoServiceDay {
  id: string;
  estimateServiceId: string;
  serviceDate: Date | string;
  dayLabel?: string;
  notes?: string;
  positions?: ExpoDayPosition[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface EstimateService {
  id: string;
  estimateTicketId: string;
  serviceType: ServiceType;
  serviceDate?: Date | string;
  duration?: number;
  staffCount?: number;
  vesselTypeId?: string;
  vesselType?: TVesselType;
  vesselId?: string;
  vessel?: { id: string; name: string; hourlyRate?: number };
  locationId?: string;
  location?: ShippingAddress;
  tempLocationData?: TempLocationData;
  inventoryTemplates?: InventoryTemplate[];
  estimatedPrice?: number;
  notes?: string;
  estimatedItemCount?: number;
  expoDays?: ExpoServiceDay[]; // Expo mode days
  createdAt: Date;
  updatedAt: Date;
}

export interface EstimateTicket {
  id: string;
  referenceNumber: string;
  clientId?: string;
  client?: TClient;
  projectId?: string;
  project?: TProject;
  tempClientData?: TempClientData;
  estimateDate: Date | string;
  status: EstimateStatus;
  createdById: string;
  createdBy?: TUser;
  services: EstimateService[];
  estimatedTotal?: number;
  notes?: string;
  convertedToJobTicketId?: string;
  createdAt: Date;
  updatedAt: Date;
}

// DTOs for API
export interface CreateEstimateServiceDto {
  estimateTicketId: string;
  serviceType: ServiceType;
  serviceDate?: string;
  duration?: number;
  staffCount?: number;
  vesselTypeId?: string;
  vesselId?: string;
  locationId?: string;
  tempLocationData?: TempLocationData;
  inventoryTemplateIds?: string[];
  estimatedPrice?: number;
  notes?: string;
  estimatedItemCount?: number;
}

export interface CreateEstimateDto {
  clientId?: string;
  projectId?: string;
  tempClientData?: TempClientData;
  estimateDate?: string;
  notes?: string;
  services?: CreateEstimateServiceDto[];
}

export interface UpdateEstimateDto {
  clientId?: string;
  tempClientData?: TempClientData;
  estimateDate?: string;
  notes?: string;
  status?: EstimateStatus;
  services?: CreateEstimateServiceDto[];
}

export interface GetEstimatesParams {
  page?: number;
  pageSize?: number;
  status?: EstimateStatus;
  clientId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface ConvertEstimateDto {
  estimateId: string;
  clientId?: string;
  projectId?: string;
}

// Expo Mode DTOs
export interface ExpoDayPositionDto {
  id?: string;
  positionId: string;
  quantity: number;
  startTime: string;
  duration: number;
  hourlyRate?: number;
}

export interface ExpoServiceDayDto {
  id?: string;
  serviceDate: string;
  dayLabel?: string;
  notes?: string;
  positions: ExpoDayPositionDto[];
}

export interface CreateExpoServiceDaysDto {
  estimateServiceId: string;
  days: ExpoServiceDayDto[];
}

export interface GenerateExpoDaysDto {
  estimateServiceId: string;
  startDate: string;
  endDate: string;
  dayLabels?: { date: string; label: string }[];
}

export interface CopyExpoDayDto {
  estimateServiceId: string;
  sourceDayId: string;
  targetDayIds?: string[];
}

// Expo Response Types
export interface ExpoDayPositionResponse {
  id: string;
  positionId: string;
  positionName: string;
  quantity: number;
  startTime: string;
  duration: number;
  hourlyRate: number;
  subtotal: number;
}

export interface ExpoServiceDayResponse {
  id: string;
  serviceDate: string;
  dayLabel: string | null;
  notes: string | null;
  dayTotal: number;
  positions: ExpoDayPositionResponse[];
}

export interface ExpoServiceSummary {
  estimateServiceId: string;
  totalDays: number;
  totalPositionAssignments: number;
  laborTotal: number;
  startDate: string;
  endDate: string;
  days: ExpoServiceDayResponse[];
}
